/* =============================================================================
   assets/rm-cart.js
   AJAX cart drawer — open/close, render items, qty changes, add to cart.
   ============================================================================= */

(function () {
  'use strict';

  /* ── State ── */
  let cartData = null;

  /* ── Currency formatter ── */
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
    minimumFractionDigits: 2,
  });
  function formatMoney(cents) { return formatter.format(cents / 100); }


  /* ── Cart API helpers ── */

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
    if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);
    cartData = await res.json();
    return cartData;
  }

  async function addToCart(payload) {
    const res  = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || 'Failed to add to cart');
    return data;
  }

  async function updateCartNote(note) {
    const res  = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || 'Failed to update note');
    cartData = data;
    return data;
  }

  async function changeLineItem(key, quantity) {
    const res  = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || 'Failed to update cart');
    cartData = data;
    return data;
  }


  /* ── Event helpers ── */
  function emit(eventName, detail = {}) {
    document.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail }));
  }

  function updateCartCountUI(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent   = count;
      el.style.display = count > 0 ? '' : 'none';
    });
    document.querySelectorAll('[data-cart-open]').forEach(el => {
      el.setAttribute('aria-label', `Cart (${count} item${count !== 1 ? 's' : ''})`);
    });
  }


  /* ── Drawer DOM refs ── */
  const drawerEl  = document.getElementById('rm-cart-drawer');
  const overlayEl = document.getElementById('rm-cart-overlay');


  /* ── Open / Close ── */
  function openDrawer() {
    if (!drawerEl) return;
    drawerEl.classList.add('is-open');
    drawerEl.setAttribute('aria-hidden', 'false');
    overlayEl?.classList.add('is-open');
    document.body.classList.add('cart-drawer-open');
    emit('cart:open');
    drawerEl.querySelector('[data-cart-close]')?.focus();

    // Restore saved note
    if (cartData) {
      const noteEl = document.getElementById('rm-cart-note');
      if (noteEl) noteEl.value = cartData.note || '';
    }
  }

  function closeDrawer() {
    if (!drawerEl) return;
    drawerEl.classList.remove('is-open');
    drawerEl.setAttribute('aria-hidden', 'true');
    overlayEl?.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');
    emit('cart:close');
  }


  /* ── Event listeners ── */
  drawerEl?.querySelector('[data-cart-close]')?.addEventListener('click', closeDrawer);
  overlayEl?.addEventListener('click', closeDrawer);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && drawerEl?.classList.contains('is-open')) closeDrawer();
  });

  // Cart note auto-save on blur
  document.getElementById('rm-cart-note')?.addEventListener('blur', async function () {
    try {
      await updateCartNote(this.value);
    } catch (err) {
      console.error('[RM Cart] Note save failed', err);
    }
  });

  // Open cart buttons
  document.querySelectorAll('[data-cart-open]').forEach(btn => {
    btn.addEventListener('click', async e => {
      e.preventDefault();
      await refreshDrawer();
      openDrawer();
    });
  });


  /* ── Render cart contents ── */
  function renderCart(cart) {
    const bodyEl   = drawerEl?.querySelector('[data-cart-body]');
    const footerEl = drawerEl?.querySelector('[data-cart-footer]');
    if (!bodyEl) return;

    updateCartCountUI(cart.item_count);

    const ethosEl = drawerEl?.querySelector('[data-cart-ethos]');

    if (cart.item_count === 0) {
      bodyEl.innerHTML = `
        <div class="rm-cart-empty">
          <p class="rm-cart-empty__msg">Your cart is empty.</p>
          <a href="/collections/all" class="rm-cart-empty__link">Continue Shopping</a>
        </div>`;
      if (footerEl) footerEl.style.display = 'none';
      ethosEl?.classList.remove('is-visible');
      return;
    }

    if (footerEl) footerEl.style.display = '';
    ethosEl?.classList.add('is-visible');

    // Build line item HTML
    const itemsHtml = cart.items.map(item => {
      const propsHtml = Object.entries(item.properties || {})
        .filter(([key]) => !key.startsWith('_'))
        .map(([key, val]) => val ? `<div class="rm-cart-item__prop"><span>${key}:</span> ${val}</div>` : '')
        .join('');

      return `
        <div class="rm-cart-item" data-key="${item.key}">
          <a href="${item.url}" class="rm-cart-item__img-wrap" tabindex="-1" aria-hidden="true">
            ${item.image
              ? `<img src="${item.image}" alt="${item.product_title}" width="80" height="80" loading="lazy">`
              : '<div class="rm-cart-item__img-placeholder"></div>'
            }
          </a>
          <div class="rm-cart-item__info">
            <a href="${item.url}" class="rm-cart-item__title">${item.product_title}</a>
            ${item.variant_title && item.variant_title !== 'Default Title'
              ? `<div class="rm-cart-item__variant">${item.variant_title}</div>`
              : ''
            }
            ${propsHtml ? `<div class="rm-cart-item__props">${propsHtml}</div>` : ''}
            <div class="rm-cart-item__bottom">
              <div class="rm-cart-qty" data-key="${item.key}">
                <button class="rm-cart-qty__btn" data-action="decrease" aria-label="Decrease quantity">&minus;</button>
                <span class="rm-cart-qty__val">${item.quantity}</span>
                <button class="rm-cart-qty__btn" data-action="increase" aria-label="Increase quantity">+</button>
              </div>
              <div class="rm-cart-item__price">${formatMoney(item.final_line_price)}</div>
            </div>
          </div>
          <button class="rm-cart-item__remove" data-key="${item.key}" aria-label="Remove ${item.product_title}">
            <svg width="13" height="14" viewBox="0 0 13 14" fill="none" aria-hidden="true">
              <path d="M1 3.5h11M4.5 3.5V2.5a1 1 0 011-1h2a1 1 0 011 1v1M5.5 6.5v4M7.5 6.5v4M2 3.5l.8 7.2a1 1 0 001 .8h5.4a1 1 0 001-.8L11 3.5"
                stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>`;
    }).join('');

    bodyEl.innerHTML = `<div class="rm-cart-items">${itemsHtml}</div>`;

    // Update footer total
    if (footerEl) {
      const totalEl = footerEl.querySelector('[data-cart-total]');
      if (totalEl) totalEl.textContent = formatMoney(cart.total_price);

      const noteEl = document.getElementById('rm-cart-note');
      if (noteEl && noteEl !== document.activeElement) noteEl.value = cart.note || '';
    }

    // Qty buttons
    bodyEl.querySelectorAll('.rm-cart-qty').forEach(qtyEl => {
      const key     = qtyEl.dataset.key;
      const valEl   = qtyEl.querySelector('.rm-cart-qty__val');
      const lineItem = cart.items.find(i => i.key === key);
      let qty       = lineItem ? lineItem.quantity : 1;

      qtyEl.querySelectorAll('.rm-cart-qty__btn').forEach(btn => {
        btn.addEventListener('click', async () => {
          qty = btn.dataset.action === 'increase' ? qty + 1 : Math.max(0, qty - 1);
          setLoading(true);
          try {
            const updated = await changeLineItem(key, qty);
            renderCart(updated);
            emit('cart:updated', { cart: updated });
          } catch (err) {
            console.error('[RM Cart] Qty change failed', err);
          } finally {
            setLoading(false);
          }
        });
      });
    });

    // Remove buttons
    bodyEl.querySelectorAll('.rm-cart-item__remove').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        setLoading(true);
        try {
          const updated = await changeLineItem(key, 0);
          renderCart(updated);
          emit('cart:updated', { cart: updated });
        } catch (err) {
          console.error('[RM Cart] Remove failed', err);
        } finally {
          setLoading(false);
        }
      });
    });
  }

  /* ── Loading state ── */
  function setLoading(on) {
    drawerEl?.classList.toggle('is-loading', on);
  }

  /* ── Refresh drawer from API ── */
  async function refreshDrawer() {
    setLoading(true);
    try {
      const cart = await fetchCart();
      renderCart(cart);
    } catch (err) {
      console.error('[RM Cart] Refresh failed', err);
    } finally {
      setLoading(false);
    }
  }


  /* ── Order form ATC submission ── */
  const orderForm = document.querySelector('.rm-band__order-form');
  if (orderForm) {
    orderForm.addEventListener('submit', async e => {
      e.preventDefault();

      const submitBtn  = orderForm.querySelector('[type="submit"]');
      const isInput    = submitBtn?.tagName === 'INPUT';
      const origLabel  = isInput ? submitBtn?.value : submitBtn?.textContent;
      const variantId  = parseInt(orderForm.querySelector('input[name="id"]')?.value, 10);

      if (!variantId) {
        console.warn('[RM Cart] No variant ID found');
        return;
      }

      const quantity   = parseInt(orderForm.querySelector('input[name="quantity"]')?.value || '1', 10);

      // Collect line item properties
      const properties = {};
      orderForm.querySelectorAll(
        'input[name^="properties["], textarea[name^="properties["], select[name^="properties["]'
      ).forEach(el => {
        const match = el.name.match(/^properties\[(.+)\]$/);
        if (match && el.value.trim() !== '') {
          properties[match[1]] = el.value.trim();
        }
      });

      if (submitBtn) {
        submitBtn.disabled = true;
        if (isInput) submitBtn.value = 'Adding\u2026';
        else submitBtn.textContent = 'Adding\u2026';
      }

      try {
        // Check for Complete the Set contour band selections
        const ctsBandInputs = orderForm.querySelectorAll('.rm-cts-band-input[data-band-variant-id]');
        const ctsBands = [];
        ctsBandInputs.forEach(inp => {
          const id = parseInt(inp.dataset.bandVariantId, 10);
          if (id) ctsBands.push(id);
        });

        let added;
        if (ctsBands.length > 0) {
          // Multi-item add — ring + all selected contour bands in one request
          const ringItem = { id: variantId, quantity };
          if (Object.keys(properties).length > 0) ringItem.properties = properties;
          const items = [ringItem, ...ctsBands.map(id => ({ id, quantity: 1 }))];
          added = await addToCart({ items });
        } else {
          const payload = { id: variantId, quantity };
          if (Object.keys(properties).length > 0) payload.properties = properties;
          added = await addToCart(payload);
        }

        emit('cart:itemAdded', { item: added, variantId, quantity, properties });
        await refreshDrawer();
        openDrawer();
      } catch (err) {
        console.error('[RM Cart] Add to cart failed:', err);

        let errorEl = orderForm.querySelector('.rm-atc-error');
        if (!errorEl) {
          errorEl = document.createElement('div');
          errorEl.className = 'rm-atc-error';
          submitBtn?.parentNode?.insertBefore(errorEl, submitBtn);
        }
        errorEl.textContent = err.message || 'Could not add to cart. Please try again.';
        setTimeout(() => { if (errorEl) errorEl.textContent = ''; }, 5000);
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          if (isInput) submitBtn.value = origLabel;
          else submitBtn.textContent = origLabel;
        }
      }
    });
  }


  /* ── Init: load cart count on page load ── */
  fetchCart()
    .then(cart => updateCartCountUI(cart.item_count))
    .catch(err => console.warn('[RM Cart] Init fetch failed', err));

})();
